import {
  HHButton,
  HHLoadingButton,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, InputAdornment } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDynamicDrawerContext } from '../../../context/dynamic-drawer.context';
import {
  useGetAllClientsQuery,
  useGetProgramCapsLazyQuery,
} from '../../../types';
import { getCurrencySymbol } from '../../../utils/currency-helpers';
import LoadingComponent from '../../utils/loading';
import {
  ProgramCapFormValues,
  ProgramCapValidationSchema,
} from './validation-schema';

export const PROGRAM_CAP_FORM_CANCEL_TEST_ID = 'program-cap-form-cancel-button';
export const PROGRAM_CAP_FORM_SUBMIT_TEST_ID = 'program-cap-form-submit-button';

interface ProgramCapFormProps {
  onSubmit: (values: ProgramCapFormValues) => void | Promise<void>;
  initialValues?: ProgramCapFormValues;
}

export const ProgramCapForm = ({
  onSubmit,
  initialValues,
}: ProgramCapFormProps): JSX.Element => {
  const { onClose } = useDynamicDrawerContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data, loading, error } = useGetAllClientsQuery();

  const [
    getProgramCaps,
    {
      data: programCapsData,
      loading: programCapsLoading,
      error: programCapsError,
    },
  ] = useGetProgramCapsLazyQuery();

  const currentYear = new Date().getFullYear();
  const defaultStartDate = new Date(currentYear, 0, 1);
  const defaultEndDate = new Date(currentYear, 11, 31);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    watch,
    trigger,
  } = useForm<ProgramCapFormValues>({
    mode: 'onChange',
    resolver: yupResolver(ProgramCapValidationSchema),
    context: {
      programCaps: programCapsData?.getProgramCaps?.items ?? [],
      editId: initialValues?.programCapId ?? null,
    },
    defaultValues: {
      programCapId: null,
      clientId: undefined,
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      cap: 995,
    },
  });

  // ACCOUNTS-AND-PLAN-MIGRATION: clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  const watchClientId = watch('clientId');

  useEffect(() => {
    const fetchProgramCaps = async (): Promise<void> => {
      await getProgramCaps({ variables: { clientId: watchClientId, page: 1 } });
      trigger(); // Trigger validation to check for overlaps
    };

    if (watchClientId) {
      fetchProgramCaps();
    }
  }, [watchClientId, getProgramCaps, trigger]);

  useEffect(() => {
    if (initialValues) {
      reset({
        programCapId: initialValues.programCapId,
        clientId: initialValues.clientId,
        startDate: initialValues.startDate,
        endDate: initialValues.endDate,
        cap: initialValues.cap,
      });
    }
  }, [initialValues, reset]);

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const handleFormSubmit = async (
    values: ProgramCapFormValues,
  ): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onSubmit(values);
    } catch {
      setIsSubmitting(false);
    }
  };

  if (loading || programCapsLoading) return <LoadingComponent center />;

  if (error || programCapsError) {
    return (
      <HHStack spacing={2}>
        <HHTypography hhVariant="body1" color="error">
          Error loading clients
        </HHTypography>
      </HHStack>
    );
  }

  return (
    <form>
      <HHStack spacing={6} direction="column">
        <Controller
          control={control}
          name="clientId"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              value={
                data?.getAllClients?.find(client => client.id === value) ?? null
              }
              options={(data?.getAllClients ?? [])
                .filter(client => client && client.name)
                .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))}
              getOptionLabel={(option): string => option.name}
              isOptionEqualToValue={(option, value): boolean =>
                option.id === value?.id
              }
              onChange={(_, newValue): void => onChange(newValue?.id ?? null)}
              disabled={!!initialValues}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="outlined"
                  label="Client"
                  error={!!errors.clientId}
                  helperText={errors.clientId?.message}
                />
              )}
            />
          )}
        />
        <HHStack direction={{ xs: 'column', md: 'row' }} spacing={6}>
          <Controller
            control={control}
            name="startDate"
            render={({ field: { onChange, value } }): JSX.Element => (
              <DatePicker
                label="Start Date"
                value={value}
                onChange={(newValue): void => {
                  onChange(newValue ? new Date(newValue) : null);
                }}
                renderInput={(params): JSX.Element => (
                  <HHTextField
                    {...params}
                    fullWidth
                    hhVariant="variant-bypass"
                    error={!!errors.startDate}
                    helperText={errors.startDate?.message}
                  />
                )}
              />
            )}
          />
          <Controller
            control={control}
            name="endDate"
            render={({ field: { onChange, value } }): JSX.Element => (
              <DatePicker
                label="End Date"
                value={value}
                onChange={(newValue): void => {
                  onChange(newValue ? new Date(newValue) : null);
                }}
                renderInput={(params): JSX.Element => (
                  <HHTextField
                    {...params}
                    fullWidth
                    hhVariant="variant-bypass"
                    error={!!errors.endDate}
                    helperText={errors.endDate?.message}
                  />
                )}
              />
            )}
          />
        </HHStack>
        <Controller
          control={control}
          name="cap"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              placeholder="995"
              hhVariant="variant-bypass"
              label="Cap"
              value={value}
              onChange={onChange}
              error={!!errors.cap}
              helperText={errors.cap?.message}
              type="number"
              inputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    disableTypography
                    sx={{ color: 'gray' }}
                  >
                    {getCurrencySymbol()}
                  </InputAdornment>
                ),
                inputMode: 'numeric',
                pattern: '[0-9]',
                min: '1',
              }}
            />
          )}
        />
        <HHStack direction="row" spacing={2} justifyContent="flex-end">
          <HHButton
            hhVariant="variant-bypass"
            variant="text"
            onClick={handleCancel}
            type="button"
            disabled={isSubmitting}
            data-testid={PROGRAM_CAP_FORM_CANCEL_TEST_ID}
          >
            Cancel
          </HHButton>
          <HHLoadingButton
            hhVariant="variant-bypass"
            variant="contained"
            type="submit"
            disabled={!isValid || !isDirty || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit(handleFormSubmit)}
            data-testid={PROGRAM_CAP_FORM_SUBMIT_TEST_ID}
          >
            Submit
          </HHLoadingButton>
        </HHStack>
      </HHStack>
    </form>
  );
};
